import { Injectable } from '@angular/core';
import { GraphQLVariablesV6, V6Client } from '@aws-amplify/api-graphql';
import { generateClient, GraphQLResult } from 'aws-amplify/api';
import { DocumentNode } from 'graphql/language';
import { from, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppsyncErrorUtil } from '../../../../essentials/util/src/appsync-error.util';
import { Logger } from '../../../../essentials/util/src/logger';

const NUMBER_OF_OPEN_SEARCH_ATTEMPTS = 3;

const logger = new Logger('AppsyncService');

@Injectable({
  providedIn: 'root',
})
export class AppsyncService {
  private readonly client: AppsyncServiceClient;

  constructor() {
    const graphqlClient = generateClient();
    this.client = new AppsyncServiceClient(graphqlClient);
  }

  getClient(): Promise<AppsyncServiceClient> {
    return Promise.resolve(this.client);
  }
}

export class AppsyncServiceClient {
  private unsubscribe$ = new Subject<void>();
  private readonly graphqlClient;

  constructor(graphqlClient: V6Client) {
    this.graphqlClient = graphqlClient;
    window.addEventListener('beforeunload', (event) => {
      this.unsubscribe$.next();
      this.unsubscribe$.complete();
      delete event['returnValue'];
    });
  }

  async query<T = any>(operation: { query: DocumentNode; variables?: GraphQLVariablesV6 }): Promise<GraphQLResult<T>> {
    return this.retryOnOpenSearchTimeout<GraphQLResult<T>>(
      async () => (await this.graphqlClient.graphql(operation)) as GraphQLResult<T>
    );
  }

  async mutate({
    mutation,
    variables,
  }: {
    mutation: DocumentNode;
    variables?: GraphQLVariablesV6;
  }): Promise<GraphQLResult<any>> {
    return this.retryOnOpenSearchTimeout(() => this.graphqlClient.graphql({ query: mutation, variables }));
  }

  subscribe({ query, variables }: { query: DocumentNode; variables?: GraphQLVariablesV6 }): Observable<any> {
    return from(this.graphqlClient.graphql({ query, variables })).pipe(takeUntil(this.unsubscribe$));
  }

  async retryOnOpenSearchTimeout<T>(apiCall: () => Promise<T>): Promise<T> {
    for (let attempt = 1; attempt <= NUMBER_OF_OPEN_SEARCH_ATTEMPTS; attempt++) {
      try {
        return await apiCall();
      } catch (e) {
        const hasOpenSearchTimeout = this.hasOpenSearchTimeout(e);
        if (!hasOpenSearchTimeout) {
          throw e;
        } else {
          logger.error(`OpenSearch:ExecutionTimeoutException on attempt ${attempt}`);
          if (attempt === NUMBER_OF_OPEN_SEARCH_ATTEMPTS) {
            throw e;
          }
        }
      }
    }
    throw Error('Error during retryOnOpenSearchTimeout, this should not be reachable');
  }

  private hasOpenSearchTimeout(caughtError: unknown): boolean {
    return (
      AppsyncErrorUtil.isAppsyncError(caughtError) &&
      caughtError.errors.map((error) => error['errorType']).includes('OpenSearch:ExecutionTimeoutException')
    );
  }
}
